/**
 * The front door.
 *
 * Three ways in, and they are genuinely different games rather than three routes
 * to the same one: alone against the computer, everyone round one phone, or
 * everyone on their own phone. The middle one is the reason the handoff screen
 * exists and the last is the reason there is a server at all.
 */

import { useState } from 'react';
import { CHIPS } from '@shared/chips';
import { MAX_PLAYERS, NAME_MAX, normalizeCode, sanitizeText } from '@shared/protocol';
import { Chip } from '../art/Chip';
import { storedName } from '../net/socket';
import { Screen } from '../ui/shell';

export type HomeChoice =
  | { kind: 'bots'; name: string; bots: number }
  | { kind: 'passPlay'; names: string[] }
  | { kind: 'host' }
  | { kind: 'join'; code: string }
  | { kind: 'rules' };

type Pane = 'front' | 'bots' | 'pass' | 'join';

export function Home({ onChoose }: { onChoose: (choice: HomeChoice) => void }) {
  const [pane, setPane] = useState<Pane>('front');

  if (pane === 'bots') return <BotsPane onBack={() => setPane('front')} onChoose={onChoose} />;
  if (pane === 'pass') return <PassPane onBack={() => setPane('front')} onChoose={onChoose} />;
  if (pane === 'join') return <JoinPane onBack={() => setPane('front')} onChoose={onChoose} />;

  return (
    <div className="screen front">
      <div className="front__crest" aria-hidden="true">
        <Chip id="excalibur" size="lg" />
      </div>
      <h1 className="wordmark">Excalibur</h1>
      <p className="tagline">Hold the sword when the music stops.</p>

      <div className="actions">
        <button type="button" className="btn" onClick={() => setPane('bots')}>
          Play the computer
        </button>
        <button type="button" className="btn" onClick={() => onChoose({ kind: 'host' })}>
          Start a room
        </button>
        <button type="button" className="btn ghost" onClick={() => setPane('join')}>
          Join with a code
        </button>
        <button type="button" className="btn ghost" onClick={() => setPane('pass')}>
          One phone, pass around
        </button>
        <button type="button" className="linkish" onClick={() => onChoose({ kind: 'rules' })}>
          How it works
        </button>
      </div>
    </div>
  );
}

function BotsPane({ onBack, onChoose }: { onBack: () => void; onChoose: (c: HomeChoice) => void }) {
  // Not "You": the narration is third person throughout ("Bedivere trashes
  // Merchant"), and a player literally named You makes every line of it
  // ungrammatical. The table marks which seat is yours instead.
  const [name, setName] = useState(() => storedName() || 'Player');
  const [bots, setBots] = useState(3);
  const clean = sanitizeText(name).slice(0, NAME_MAX) || 'Player';

  return (
    <Screen
      eyebrow="Alone"
      title="Play the computer"
      actions={
        <>
          <button type="button" className="btn" onClick={() => onChoose({ kind: 'bots', name: clean, bots })}>
            Deal for {bots + 1}
          </button>
          <button type="button" className="btn ghost" onClick={onBack}>
            Back
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field__label">Your name</span>
        <input className="field__input" value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX} />
      </label>

      <div className="stepper">
        <button
          type="button"
          className="stepper__btn"
          onClick={() => setBots((n) => Math.max(1, n - 1))}
          disabled={bots <= 1}
          aria-label="One fewer opponent"
        >
          −
        </button>
        <span className="stepper__value" aria-live="polite">
          {bots} {bots === 1 ? 'opponent' : 'opponents'}
        </span>
        <button
          type="button"
          className="stepper__btn"
          onClick={() => setBots((n) => Math.min(MAX_PLAYERS - 1, n + 1))}
          disabled={bots >= MAX_PLAYERS - 1}
          aria-label="One more opponent"
        >
          +
        </button>
      </div>
      <p className="muted">
        They play from the same hidden information you do — they cannot see Avalon, and they cannot see
        your hand.
      </p>
    </Screen>
  );
}

function PassPane({ onBack, onChoose }: { onBack: () => void; onChoose: (c: HomeChoice) => void }) {
  const [names, setNames] = useState<string[]>(['', '']);
  const clean = names.map((n) => sanitizeText(n).slice(0, NAME_MAX)).filter(Boolean);
  const unique = new Set(clean.map((n) => n.toLowerCase())).size === clean.length;
  const ready = clean.length >= 2 && unique;

  return (
    <Screen
      eyebrow="One phone"
      title="Who is playing?"
      actions={
        <>
          <button type="button" className="btn" disabled={!ready} onClick={() => onChoose({ kind: 'passPlay', names: clean })}>
            {clean.length < 2 ? 'Two players minimum' : unique ? `Deal for ${clean.length}` : 'Names must differ'}
          </button>
          <button type="button" className="btn ghost" onClick={onBack}>
            Back
          </button>
        </>
      }
    >
      <p className="muted">
        The phone goes round the table. Each player holds a button to see their own hand, so nobody
        catches a glimpse of anyone else&rsquo;s.
      </p>
      {names.map((n, i) => (
        <label className="field" key={i}>
          <span className="field__label">Player {i + 1}</span>
          <input
            className="field__input"
            value={n}
            maxLength={NAME_MAX}
            onChange={(e) => setNames((cur) => cur.map((x, j) => (j === i ? e.target.value : x)))}
          />
        </label>
      ))}
      <div className="prompt__row">
        {names.length < MAX_PLAYERS ? (
          <button type="button" className="btn ghost" onClick={() => setNames((cur) => [...cur, ''])}>
            Add a player
          </button>
        ) : null}
        {names.length > 2 ? (
          <button type="button" className="btn ghost" onClick={() => setNames((cur) => cur.slice(0, -1))}>
            Remove the last
          </button>
        ) : null}
      </div>
    </Screen>
  );
}

function JoinPane({ onBack, onChoose }: { onBack: () => void; onChoose: (c: HomeChoice) => void }) {
  const [code, setCode] = useState('');
  const clean = normalizeCode(code);
  const ready = clean.length >= 4;

  return (
    <Screen
      eyebrow="Room"
      title="Join a table"
      actions={
        <>
          <button type="button" className="btn" disabled={!ready} onClick={() => onChoose({ kind: 'join', code: clean })}>
            Join
          </button>
          <button type="button" className="btn ghost" onClick={onBack}>
            Back
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field__label">Room code</span>
        <input
          className="field__input field__input--code"
          value={clean}
          onChange={(e) => setCode(e.target.value)}
          // A four-character code is not a word; the keyboard should not try to
          // correct it, capitalise it, or offer to complete it.
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
          enterKeyHint="go"
          maxLength={6}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) onChoose({ kind: 'join', code: clean });
          }}
        />
      </label>
      <p className="muted">Four letters and numbers, from whoever started the room.</p>
    </Screen>
  );
}

/** A quiet reference, reachable from the front door and from a game in progress. */
export function Rules({ onBack }: { onBack: () => void }) {
  const order = Object.values(CHIPS).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  return (
    <Screen
      eyebrow="Reference"
      title="How it works"
      actions={
        <button type="button" className="btn ghost" onClick={onBack}>
          Back
        </button>
      }
    >
      <p>
        Sixty-two chips are spread facedown in the middle of the table. Three blade replicas divide
        them into three <strong>regions</strong>; together they are called <strong>Avalon</strong>.
      </p>
      <ol className="steps">
        <li>
          <strong>Draw.</strong> Everyone tops their hand back up to four, taking from any region.
        </li>
        <li>
          <strong>Play.</strong> Everyone conceals one chip. Swords can never be played.
        </li>
        <li>
          <strong>Reveal.</strong> Fists open. On Reveal and Permanent chips fire first.
        </li>
        <li>
          <strong>Resolve.</strong> Starting at the crown and going clockwise, each chip does its work
          and is then trashed faceup.
        </li>
        <li>
          <strong>Pass.</strong> The crown moves one seat to the left.
        </li>
      </ol>
      <p>
        The moment a region runs empty — two regions with six or more players — the round you are in
        becomes the last one.
      </p>
      <h2 className="section-title">Winning</h2>
      <p>
        Whoever holds <strong>Excalibur</strong> at the end takes the crown. If nobody does, the
        kingdom falls to chaos and everybody loses. Whoever holds the <strong>Cursed Blade</strong> is
        the ultimate loser — and if one hand holds both, everyone else shares a tainted win. The{' '}
        <strong>Squire Sword</strong> wins alongside the player to its holder&rsquo;s left, except at
        two and three players.
      </p>

      <h2 className="section-title">Every chip</h2>
      <div className="reference-grid">
        {order.map((c) => (
          <Chip key={c.id} id={c.id} size="lg" showText showCount />
        ))}
      </div>
    </Screen>
  );
}
